import {
  formatPackSizeMiB,
  LARGE_PACK_WARN_BYTES,
  VERY_LARGE_PACK_WARN_BYTES,
} from "../io/git/workspacePackStats.js";
import { parseShellWords, runGitTerminalLine } from "../io/git/gitTerminalShell.js";
import type { GitNetworkProgress } from "../io/git/gitWorkspaceOperations.js";
import type { DiffEditorSnapshot } from "./diffTabMonaco.js";

export type GitTerminalPanelOptions = {
  /** Called after commands that change refs or the working tree. */
  onGitMutation: () => void | Promise<void>;
  /**
   * Called when the user confirms opening one or more compare tabs from `git diff`.
   * Always invoked synchronously on the microtask after the confirmation click.
   */
  onOpenGitDiffBatch?: (snapshots: DiffEditorSnapshot[]) => void;
  /**
   * Called when `git show <ref>:<path>` reads a file at a commit.
   * Host should open it as a new editable text tab.
   */
  onOpenTextTab?: (tab: { title: string; content: string; language: import("../doc/languageMode.js").LanguageMode }) => void;
  /** User clicked the panel close control (host should hide). */
  onRequestClose: () => void;
  /** Handle `git clone` by picking/opening a folder workspace. */
  onCloneRequested: (
    url: string,
    directoryName?: string,
    onProgress?: (p: GitNetworkProgress) => void,
  ) => Promise<void>;
  /** Surface operation progress in the app status line. */
  onOperationStatus?: (message: string) => void;
};

/**
 * Bottom “terminal” view for `git` CLI-style lines (backed by isomorphic-git on the folder workspace root).
 */
export class GitTerminalPanel {
  readonly element: HTMLElement;
  private readonly options: GitTerminalPanelOptions;
  private readonly outputEl: HTMLElement;
  private readonly inputEl: HTMLInputElement;
  private readonly promptUser: string;
  private root: FileSystemDirectoryHandle | null = null;
  /** True when `.git` exists (Git may still fail in-tab on huge packfiles). */
  private isGitRepo = false;
  private cwdLabel = "~";
  private running = false;
  private lastWorkspaceBannerKey = "";

  constructor(options: GitTerminalPanelOptions) {
    this.options = options;
    this.promptUser = "npp";

    this.element = document.createElement("div");
    this.element.className = "npp-git-terminal";
    this.element.setAttribute("role", "region");
    this.element.setAttribute("aria-label", "Git terminal");

    const head = document.createElement("div");
    head.className = "npp-git-terminal__head";
    const title = document.createElement("span");
    title.className = "npp-git-terminal__title";
    title.textContent = "Git terminal";
    const hint = document.createElement("span");
    hint.className = "npp-git-terminal__head-hint";
    hint.textContent = "isomorphic-git · folder workspace";
    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "npp-git-terminal__close";
    closeBtn.setAttribute("aria-label", "Close Git terminal");
    closeBtn.title = "Close";
    closeBtn.textContent = "×";
    closeBtn.addEventListener("click", () => this.options.onRequestClose());
    head.append(title, hint, closeBtn);

    this.outputEl = document.createElement("div");
    this.outputEl.className = "npp-git-terminal__output";
    this.outputEl.setAttribute("aria-live", "polite");

    const inputRow = document.createElement("div");
    inputRow.className = "npp-git-terminal__input-row";
    const promptEl = document.createElement("span");
    promptEl.className = "npp-git-terminal__prompt";
    promptEl.textContent = this.renderPrompt();
    this.inputEl = document.createElement("input");
    this.inputEl.type = "text";
    this.inputEl.className = "npp-git-terminal__input";
    this.inputEl.spellcheck = false;
    this.inputEl.autocomplete = "off";
    this.inputEl.setAttribute("aria-label", "Git command line");
    this.inputEl.placeholder = "git status";
    this.inputEl.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        void this.submitLine();
      }
    });
    inputRow.append(promptEl, this.inputEl);

    this.element.append(head, this.outputEl, inputRow);
  }

  /**
   * @param largestPackBytes largest `.git/objects/pack/*.pack` size, or 0 if not a git repo / unknown.
   */
  setWorkspace(
    root: FileSystemDirectoryHandle | null,
    isGitRepo: boolean,
    largestPackBytes = 0,
  ): void {
    this.root = root;
    this.cwdLabel = root?.name ?? "~";

    const hasGitMetadata = Boolean(root && isGitRepo);
    this.isGitRepo = hasGitMetadata;

    this.inputEl.disabled = this.running;
    this.updatePromptDisplay();

    const key = `${root?.name ?? ""}:g${isGitRepo}:p${largestPackBytes}`;
    if (key === this.lastWorkspaceBannerKey) return;
    this.lastWorkspaceBannerKey = key;

    if (!root) {
      this.appendMetaLine("No folder workspace — open one via File → Open Folder as Workspace…");
      return;
    }
    if (!isGitRepo) {
      this.appendMetaLine(`Folder “${root.name}” is not a git repository (no .git).`);
      return;
    }
    this.appendMetaLine(`Git commands run in “${root.name}” (browser / isomorphic-git, not system git).`);
    if (largestPackBytes >= VERY_LARGE_PACK_WARN_BYTES) {
      this.appendMetaLine(
        `Largest packfile ~${formatPackSizeMiB(largestPackBytes)} — isomorphic-git loads the whole pack into this tab’s memory; close other tabs or use desktop git if commands fail or the tab reloads.`,
      );
    } else if (largestPackBytes >= LARGE_PACK_WARN_BYTES) {
      this.appendMetaLine(
        `Largest packfile ~${formatPackSizeMiB(largestPackBytes)} — some commands may fail if this tab runs out of memory.`,
      );
    }
  }

  clearOutput(): void {
    this.outputEl.replaceChildren();
  }

  focusCommandInput(): void {
    this.inputEl.focus();
  }

  private renderPrompt(): string {
    return `${this.promptUser}@${this.cwdLabel}$`;
  }

  private updatePromptDisplay(): void {
    const row = this.element.querySelector(".npp-git-terminal__input-row");
    const promptEl = row?.querySelector(".npp-git-terminal__prompt");
    if (promptEl) promptEl.textContent = this.renderPrompt();
  }

  private appendMetaLine(text: string): void {
    const line = document.createElement("div");
    line.className = "npp-git-terminal__line npp-git-terminal__line--meta";
    line.textContent = text;
    this.outputEl.appendChild(line);
    this.scrollOutputToEnd();
  }

  private appendCommandLine(text: string): void {
    const line = document.createElement("div");
    line.className = "npp-git-terminal__line npp-git-terminal__line--cmd";
    line.textContent = `${this.renderPrompt()} ${text}`;
    this.outputEl.appendChild(line);
    this.scrollOutputToEnd();
  }

  private appendStdout(text: string): void {
    if (!text) return;
    const line = document.createElement("pre");
    line.className = "npp-git-terminal__line npp-git-terminal__line--out";
    line.textContent = text;
    this.outputEl.appendChild(line);
    this.scrollOutputToEnd();
  }

  private appendStderr(text: string): void {
    const line = document.createElement("pre");
    line.className = "npp-git-terminal__line npp-git-terminal__line--err";
    line.textContent = text;
    this.outputEl.appendChild(line);
    this.scrollOutputToEnd();
  }

  /**
   * Show a live "still running" line so long operations (clone/pull/fetch) are visibly active.
   * Returns a stopper that finalizes the line as done/failed.
   */
  private startProgressLine(label: string): (ok: boolean) => void {
    const line = document.createElement("div");
    line.className = "npp-git-terminal__line npp-git-terminal__line--meta";
    const startedAt = Date.now();
    const render = (): void => {
      const sec = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
      line.textContent = `${label}... ${sec}s`;
    };
    render();
    this.outputEl.appendChild(line);
    this.scrollOutputToEnd();
    const timer = setInterval(() => {
      render();
      this.scrollOutputToEnd();
    }, 1000);
    return (ok: boolean): void => {
      clearInterval(timer);
      const sec = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
      line.textContent = ok ? `${label}... done (${sec}s)` : `${label}... failed (${sec}s)`;
      this.scrollOutputToEnd();
    };
  }

  private formatNetworkProgress(p: GitNetworkProgress): string | null {
    const msg = String(p.message ?? "").trim();
    if (msg) return msg;
    const phase = String(p.phase ?? "").trim();
    const loaded = Number(p.loaded ?? NaN);
    const total = Number(p.total ?? NaN);
    if (!phase && !Number.isFinite(loaded) && !Number.isFinite(total)) return null;
    if (Number.isFinite(loaded) && Number.isFinite(total) && total > 0) {
      const pct = Math.max(0, Math.min(100, Math.floor((loaded / total) * 100)));
      return `${phase || "progress"}: ${pct}% (${loaded}/${total})`;
    }
    if (Number.isFinite(loaded)) return `${phase || "progress"}: ${loaded}`;
    return phase || null;
  }

  private startNetworkProgressLine(label: string): {
    update: (p: GitNetworkProgress) => void;
    stop: (ok: boolean) => void;
  } {
    const line = document.createElement("div");
    line.className = "npp-git-terminal__line npp-git-terminal__line--meta";
    line.textContent = `${label}...`;
    this.outputEl.appendChild(line);
    this.scrollOutputToEnd();
    const update = (p: GitNetworkProgress): void => {
      const text = this.formatNetworkProgress(p);
      if (!text) return;
      line.textContent = `${label}: ${text}`;
      this.scrollOutputToEnd();
    };
    const stop = (ok: boolean): void => {
      if (ok) {
        const cur = line.textContent ?? label;
        line.textContent = cur.includes("done") ? cur : `${cur} (done)`;
      } else {
        line.textContent = `${label}: failed`;
      }
      this.scrollOutputToEnd();
    };
    return { update, stop };
  }

  private scrollOutputToEnd(): void {
    this.outputEl.scrollTop = this.outputEl.scrollHeight;
  }

  private async submitLine(): Promise<void> {
    const raw = this.inputEl.value;
    this.inputEl.value = "";
    const line = raw.trim();
    if (!line) return;

    const low = line.toLowerCase();
    if (low === "clear" || low === "cls") {
      this.clearOutput();
      this.appendMetaLine("(cleared)");
      return;
    }

    this.appendCommandLine(raw.trimEnd());

    const argv = parseShellWords(line);
    if (argv[0]?.toLowerCase() === "git" && argv[1]?.toLowerCase() === "clone") {
      const url = argv[2];
      const directoryName = argv[3];
      if (!url) {
        this.appendStderr("usage: git clone <url> [directory]");
        return;
      }
      this.running = true;
      this.inputEl.disabled = true;
      const op = this.startNetworkProgressLine("Clone");
      const stopProgress = this.startProgressLine("Cloning repository");
      this.options.onOperationStatus?.(`Git: cloning ${url}...`);
      try {
        await this.options.onCloneRequested(url, directoryName, (p) => op.update(p));
        stopProgress(true);
        op.stop(true);
        this.options.onOperationStatus?.("Git: clone completed");
        this.appendMetaLine("(ok)");
      } catch (e) {
        stopProgress(false);
        op.stop(false);
        this.options.onOperationStatus?.("Git: clone failed");
        this.appendStderr(e instanceof Error ? e.message : String(e));
      } finally {
        this.running = false;
        this.inputEl.disabled = false;
        this.inputEl.focus();
      }
      return;
    }

    if (!this.root || !this.isGitRepo) {
      this.appendStderr("No git folder workspace. Use File → Open Folder as Workspace… on a repository.");
      return;
    }

    if (this.running) {
      this.appendStderr("Previous command still running.");
      return;
    }

    this.running = true;
    this.inputEl.disabled = true;
    const op = this.startNetworkProgressLine("Git");
    const stopProgress = this.startProgressLine(`Running ${line}`);
    this.options.onOperationStatus?.(`Git: running ${line}...`);
    try {
      const r = await runGitTerminalLine(this.root, line, {
        onProgress: (p) => op.update(p),
      });
      stopProgress(r.exitCode === 0);
      op.stop(r.exitCode === 0);
      if (r.stdout) this.appendStdout(r.stdout);
      if (r.stderr) this.appendStderr(r.stderr);
      if (r.exitCode === 0 && !r.stdout && !r.stderr && !r.openDiffSnapshots?.length) {
        this.appendMetaLine("(ok)");
      }
      if (r.exitCode === 0) {
        this.options.onOperationStatus?.(`Git: completed ${line}`);
      } else {
        this.options.onOperationStatus?.(`Git: failed ${line} (exit ${r.exitCode})`);
      }
      if (r.exitCode === 0 && r.openDiffSnapshots?.length) {
        this.appendDiffConfirmation(r.openDiffSnapshots, r.openDiffSnapshotsTruncatedNote);
      }
      if (r.exitCode === 0 && r.openTextTab) {
        this.options.onOpenTextTab?.(r.openTextTab);
      }
      if (r.workspaceMutated) await this.options.onGitMutation();
    } catch (e) {
      stopProgress(false);
      op.stop(false);
      this.options.onOperationStatus?.(`Git: failed ${line}`);
      this.appendStderr(e instanceof Error ? e.message : String(e));
    } finally {
      this.running = false;
      this.inputEl.disabled = false;
      this.inputEl.focus();
    }
  }

  /**
   * Renders an inline confirmation block inside the terminal output.
   * - Single snapshot: opens immediately without any confirmation UI.
   * - Multiple snapshots: shows a count badge, file list with checkboxes, and an "Open" button.
   */
  private appendDiffConfirmation(snapshots: DiffEditorSnapshot[], truncatedNote?: string): void {
    if (snapshots.length === 0) return;

    // Single file: open without asking.
    if (snapshots.length === 1) {
      const snap = snapshots[0]!;
      this.options.onOpenGitDiffBatch?.([snap]);
      this.appendMetaLine(`Opened compare tab: ${snap.leftLabel} ↔ ${snap.rightLabel}`);
      return;
    }

    // Multiple files: show confirmation widget.
    const block = document.createElement("div");
    block.className = "npp-git-diff-confirm";

    // Header row: count badge + label.
    const header = document.createElement("div");
    header.className = "npp-git-diff-confirm__header";

    const badge = document.createElement("span");
    badge.className = "npp-git-diff-confirm__badge";
    badge.textContent = String(snapshots.length);

    const label = document.createElement("span");
    label.className = "npp-git-diff-confirm__label";
    label.textContent = `${snapshots.length} compare tab${snapshots.length !== 1 ? "s" : ""} ready`;
    if (truncatedNote) label.textContent += ` ${truncatedNote}`;

    header.append(badge, label);
    block.append(header);

    // Visual count dots (up to 20 shown, rest summarised).
    const dots = document.createElement("div");
    dots.className = "npp-git-diff-confirm__dots";
    const MAX_DOTS = 20;
    const shown = Math.min(snapshots.length, MAX_DOTS);
    for (let i = 0; i < shown; i++) {
      const d = document.createElement("span");
      d.className = "npp-git-diff-confirm__dot";
      dots.append(d);
    }
    if (snapshots.length > MAX_DOTS) {
      const more = document.createElement("span");
      more.className = "npp-git-diff-confirm__dot-more";
      more.textContent = `+${snapshots.length - MAX_DOTS}`;
      dots.append(more);
    }
    block.append(dots);

    // File list with checkboxes.
    const list = document.createElement("div");
    list.className = "npp-git-diff-confirm__list";
    const checkboxes: HTMLInputElement[] = [];

    for (const snap of snapshots) {
      const row = document.createElement("label");
      row.className = "npp-git-diff-confirm__item";

      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = true;
      cb.className = "npp-git-diff-confirm__cb";
      checkboxes.push(cb);

      // Derive a short filename from the right label (e.g. "worktree — src/App.ts" → "src/App.ts").
      const filePart = snap.rightLabel.replace(/^.*?—\s*/, "").trim() || snap.rightLabel;
      const nameEl = document.createElement("span");
      nameEl.className = "npp-git-diff-confirm__filename";
      nameEl.textContent = filePart;
      nameEl.title = `${snap.leftLabel} ↔ ${snap.rightLabel}`;

      row.append(cb, nameEl);
      list.append(row);

      // Keep the dot in sync with the checkbox.
      const dotEl = dots.children[checkboxes.length - 1] as HTMLElement | undefined;
      cb.addEventListener("change", () => {
        dotEl?.classList.toggle("npp-git-diff-confirm__dot--unchecked", !cb.checked);
        updateOpenBtn();
      });
    }
    block.append(list);

    // Action buttons.
    const actions = document.createElement("div");
    actions.className = "npp-git-diff-confirm__actions";

    const openBtn = document.createElement("button");
    openBtn.type = "button";
    openBtn.className = "npp-git-diff-confirm__open-btn";

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "npp-git-diff-confirm__cancel-btn";
    cancelBtn.textContent = "Cancel";

    const updateOpenBtn = (): void => {
      const count = checkboxes.filter((c) => c.checked).length;
      openBtn.textContent = count > 0 ? `Open ${count} tab${count !== 1 ? "s" : ""}` : "Open 0 tabs";
      openBtn.disabled = count === 0;
    };
    updateOpenBtn();

    openBtn.addEventListener("click", () => {
      const selected = snapshots.filter((_, i) => checkboxes[i]?.checked);
      block.remove();
      if (selected.length > 0) {
        this.options.onOpenGitDiffBatch?.(selected);
        this.appendMetaLine(
          `Opened ${selected.length} compare tab${selected.length !== 1 ? "s" : ""} from git diff.`,
        );
      }
      this.scrollOutputToEnd();
    });

    cancelBtn.addEventListener("click", () => {
      block.remove();
      this.appendMetaLine("(cancelled)");
      this.scrollOutputToEnd();
    });

    actions.append(openBtn, cancelBtn);
    block.append(actions);

    this.outputEl.appendChild(block);
    this.scrollOutputToEnd();
  }
}
